
import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  CalendarDays,
  ExternalLink,
  Film,
  Loader2,
  Play,
  RefreshCw,
  Sparkles,
  Video,
  X,
} from "lucide-react";

import { api, resolveMediaUrl } from "../lib/api";

/* ==========================================
   HELPER FUNCTIONS
========================================== */

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
}

function getRecords(response) {
  if (Array.isArray(response)) return response;

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
}

function getYouTubeId(url) {
  if (!url) return "";

  try {
    const parsed = new URL(
      url,
      "https://www.youtube.com"
    );

    const hostname = parsed.hostname
      .toLowerCase()
      .replace(/^www\./, "");

    if (
      hostname === "youtu.be" ||
      hostname === "m.youtu.be"
    ) {
      return parsed.pathname.split("/").filter(Boolean)[0] || "";
    }

    if (
      hostname === "youtube.com" ||
      hostname === "m.youtube.com" ||
      hostname === "youtube-nocookie.com"
    ) {
      if (parsed.pathname.startsWith("/embed/")) {
        return parsed.pathname.split("/")[2] || "";
      }

      if (parsed.pathname.startsWith("/shorts/")) {
        return parsed.pathname.split("/")[2] || "";
      }

      if (parsed.pathname.startsWith("/live/")) {
        return parsed.pathname.split("/")[2] || "";
      }

      return parsed.searchParams.get("v") || "";
    }
  } catch {
    return "";
  }

  return "";
}

function getEmbedUrl(url) {
  const id = getYouTubeId(url);

  return /^[a-zA-Z0-9_-]{11}$/.test(id)
    ? `https://www.youtube-nocookie.com/embed/${id}`
    : "";
}

function isDirectVideo(url) {
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url || "");
}

function getThumbnail(item) {
  const thumbnail =
    item.thumbnail?.url ||
    item.thumbnail?.path ||
    (typeof item.thumbnail === "string"
      ? item.thumbnail
      : "");

  if (thumbnail) {
    return resolveMediaUrl(thumbnail);
  }

  const youtubeId = getYouTubeId(item.videoUrl);

  return /^[a-zA-Z0-9_-]{11}$/.test(youtubeId)
    ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
    : "";
}

/* ==========================================
   VIDEO CARD
========================================== */

function VideoCard({ item, index, onPlay }) {
  const thumbnail = getThumbnail(item);
  const videoUrl = item.videoUrl || "";

  const canPlayInline =
    Boolean(getEmbedUrl(videoUrl)) ||
    isDirectVideo(videoUrl);

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{
        duration: 0.4,
        delay: Math.min(index * 0.06, 0.18),
      }}
      className="group flex h-full flex-col overflow-hidden rounded-[22px] border border-red-100 bg-white shadow-[0_10px_35px_rgba(80,25,25,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-red-200 hover:shadow-[0_18px_45px_rgba(80,25,25,0.12)]"
    >
      {/* VIDEO THUMBNAIL */}
      <button
        type="button"
        onClick={() => onPlay(item)}
        aria-label={`Play ${item.title || "school video"}`}
        className="relative block aspect-video w-full overflow-hidden bg-[#231414]"
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={
              item.thumbnail?.alt ||
              item.title ||
              "School video"
            }
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.045]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-red-100 via-white to-amber-50">
            <Film
              size={48}
              className="text-red-300"
              strokeWidth={1.3}
            />
          </div>
        )}

        {/* PLAY BUTTON */}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-white bg-red-800/95 text-white shadow-xl transition-transform duration-300 group-hover:scale-110">
            {canPlayInline ? (
              <Play
                size={25}
                fill="currentColor"
                className="ml-1"
              />
            ) : (
              <ExternalLink size={24} />
            )}
          </span>
        </span>

        {/* CORNER LABEL */}
        <span className="absolute left-3 top-3 rounded-full border border-white/30 bg-white/95 px-3 py-1 text-[10px] font-medium text-red-800 shadow-sm">
          {item.category || "School Video"}
        </span>
      </button>

      {/* RED / GOLD ACCENT */}
      <div className="h-[3px] bg-gradient-to-r from-red-800 via-red-500 to-amber-400" />

      {/* CARD INFORMATION */}
      <div className="flex flex-1 flex-col p-5">
        <h2 className="font-serif text-[19px] font-medium leading-snug text-slate-900 transition-colors group-hover:text-red-800">
          {item.title || "School Video"}
        </h2>

        {item.description && (
          <p className="mt-3 line-clamp-3 flex-1 whitespace-pre-line text-[13px] font-normal leading-6 text-slate-600">
            {item.description}
          </p>
        )}

        <div className="mt-auto pt-4">
          <div className="mb-4 h-px bg-gradient-to-r from-red-100 via-slate-100 to-transparent" />

          <div className="flex flex-wrap items-center justify-between gap-3">
            {item.date ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                <CalendarDays
                  size={14}
                  className="text-amber-600"
                />
                {formatDate(item.date)}
              </span>
            ) : (
              <span className="text-xs text-slate-400">
                School Gallery
              </span>
            )}

            <button
              type="button"
              onClick={() => onPlay(item)}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-red-800 transition hover:text-red-600"
            >
              {canPlayInline ? "Watch Video" : "Open Video"}
              <Play size={14} />
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

/* ==========================================
   VIDEO GALLERY
========================================== */

export default function VideoGallery() {
  const [videos, setVideos] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);

  /* ==========================================
     LOAD VIDEO DATA
  ========================================== */

  useEffect(() => {
    let active = true;

    async function loadVideos() {
      setLoading(true);
      setError("");

      try {
        const response = await api.activeVideos();

        if (!active) return;

        const records = getRecords(response).filter(
          (item) => item.isActive !== false
        );

        setVideos(records);
      } catch (requestError) {
        if (!active) return;

        setError(
          requestError?.message ||
            "Unable to load school videos."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadVideos();

    return () => {
      active = false;
    };
  }, [refreshKey]);

  /* ==========================================
     CATEGORY FILTERS
  ========================================== */

  const categories = useMemo(() => {
    return [
      "All",
      ...new Set(
        videos
          .map((item) => item.category)
          .filter(Boolean)
      ),
    ];
  }, [videos]);

  const filteredVideos = useMemo(() => {
    return category === "All"
      ? videos
      : videos.filter(
          (item) => item.category === category
        );
  }, [category, videos]);

  /* ==========================================
     VIDEO PLAYER
  ========================================== */

  function openVideo(item) {
    const url = item.videoUrl || "";

    if (
      getEmbedUrl(url) ||
      isDirectVideo(url)
    ) {
      setSelectedVideo(item);
    } else if (url) {
      // External videos retain their original URL.
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }

  function closeVideo() {
    setSelectedVideo(null);
  }

  useEffect(() => {
    if (!selectedVideo) return;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setSelectedVideo(null);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedVideo]);

  const selectedEmbedUrl = selectedVideo
    ? getEmbedUrl(selectedVideo.videoUrl)
    : "";

  const selectedDirectUrl =
    selectedVideo &&
    isDirectVideo(selectedVideo.videoUrl)
      ? resolveMediaUrl(selectedVideo.videoUrl)
      : "";

  /* ==========================================
     PAGE
  ========================================== */

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#FFFCF9] pb-16">

      {/* DECORATIVE BACKGROUND */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-[350px] h-80 w-80 rounded-full bg-red-100/50 blur-3xl" />
        <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-amber-100/50 blur-3xl" />
      </div>

      {/* PAGE HEADER */}
      <section className="relative overflow-hidden bg-gradient-to-br from-red-900 via-red-800 to-[#b42323] px-4 pb-24 pt-12 text-white sm:px-6 lg:px-8">

        <div className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full border-[44px] border-white/5" />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mx-auto max-w-7xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-white/10 px-4 py-2 text-[11px] font-normal tracking-wide text-amber-100">
            <Sparkles size={15} />
            Life at St. Xavier&apos;s
          </span>

          <h1 className="mt-5 font-serif text-[33px] font-medium leading-tight sm:text-[43px] lg:text-[51px]">
            Video{" "}
            <span className="text-amber-300">
              Gallery
            </span>
          </h1>

          <div className="mx-auto mt-4 h-1 w-20 rounded-full bg-amber-400" />

          <p className="mx-auto mt-5 max-w-2xl text-sm font-normal leading-7 text-white/85 sm:text-[15px]">
            Explore school activities, celebrations,
            learning experiences and memorable moments
            through our video collection.
          </p>
        </motion.div>
      </section>

      {/* MAIN CONTENT */}
      <div className="relative z-10 mx-auto -mt-11 max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* CATEGORY PANEL */}
        <div className="mb-7 rounded-[20px] border border-red-100 bg-white px-4 py-5 shadow-[0_12px_35px_rgba(80,25,25,0.07)] sm:px-6">

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">

            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-800">
                <Video size={20} />
              </div>

              <div>
                <h2 className="font-serif text-lg font-medium text-slate-900">
                  Explore School Videos
                </h2>

                <p className="mt-1 text-xs text-slate-500">
                  Browse videos by category
                </p>
              </div>
            </div>

            {!loading && !error && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                {filteredVideos.length}{" "}
                {filteredVideos.length === 1
                  ? "Video"
                  : "Videos"}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((item) => {
              const active = category === item;

              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCategory(item)}
                  className={`rounded-full border px-4 py-2 text-[12px] font-medium transition-all ${
                    active
                      ? "border-red-800 bg-red-800 text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-600 hover:border-red-200 hover:bg-red-50 hover:text-red-800"
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>

        {/* LOADING */}
        {loading ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-red-100 bg-white shadow-sm">
            <Loader2
              size={32}
              className="animate-spin text-red-800"
            />

            <p className="mt-4 text-sm text-slate-500">
              Loading school videos...
            </p>
          </div>
        ) : error ? (
          /* ERROR */
          <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-white px-6 py-10 text-center shadow-sm">

            <AlertCircle
              size={34}
              className="mx-auto text-red-700"
            />

            <h2 className="mt-3 font-serif text-xl font-medium text-slate-900">
              Unable to Load Videos
            </h2>

            <p className="mt-2 text-sm text-red-700">
              {error}
            </p>

            <button
              type="button"
              onClick={() =>
                setRefreshKey((value) => value + 1)
              }
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-red-800 px-5 py-2.5 text-sm text-white hover:bg-red-900"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        ) : filteredVideos.length === 0 ? (
          /* EMPTY */
          <div className="rounded-2xl border border-red-100 bg-white px-6 py-16 text-center shadow-sm">

            <Film
              size={44}
              className="mx-auto text-red-200"
            />

            <h2 className="mt-4 font-serif text-xl font-medium text-slate-900">
              No Videos Available
            </h2>

            <p className="mt-2 text-sm text-slate-500">
              No videos were found in this category.
            </p>
          </div>
        ) : (
          /* VIDEO GRID */
          <div className="grid items-stretch gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredVideos.map((item, index) => (
              <VideoCard
                key={item._id || `${item.title}-${index}`}
                item={item}
                index={index}
                onPlay={openVideo}
              />
            ))}
          </div>
        )}
      </div>

      {/* ======================================
          VIDEO PLAYER MODAL
      ====================================== */}

      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            key="video-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-label="School video player"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#130a0a]/95 p-3 backdrop-blur-md sm:p-6"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeVideo();
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-5xl overflow-hidden rounded-[20px] bg-white shadow-2xl"
            >

              {/* MODAL HEADER */}
              <div className="flex items-center justify-between gap-4 bg-gradient-to-r from-red-900 to-red-800 px-4 py-4 text-white sm:px-6">

                <div className="flex min-w-0 items-center gap-3">
                  <Video
                    size={20}
                    className="shrink-0 text-amber-300"
                  />

                  <h3 className="truncate font-serif text-[15px] font-medium sm:text-lg">
                    {selectedVideo.title || "School Video"}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={closeVideo}
                  aria-label="Close video"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 transition hover:bg-white/20"
                >
                  <X size={19} />
                </button>
              </div>

              {/* VIDEO */}
              <div className="aspect-video w-full bg-black">
                {selectedEmbedUrl ? (
                  <iframe
                    src={selectedEmbedUrl}
                    title={selectedVideo.title || "School Video"}
                    className="h-full w-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                ) : selectedDirectUrl ? (
                  <video
                    key={selectedDirectUrl}
                    controls
                    autoPlay
                    playsInline
                    poster={getThumbnail(selectedVideo) || undefined}
                    className="h-full w-full object-contain"
                  >
                    <source src={selectedDirectUrl} />
                    Your browser does not support video playback.
                  </video>
                ) : null}
              </div>

              {/* MODAL FOOTER */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
                <span className="rounded-full border border-red-100 bg-red-50 px-3 py-1.5 text-xs text-red-800">
                  {selectedVideo.category || "School Video"}
                </span>

                {selectedVideo.date && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                    <CalendarDays
                      size={14}
                      className="text-amber-700"
                    />
                    {formatDate(selectedVideo.date)}
                  </span>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
